import type { CSSProperties } from 'react';
import { cn } from '../lib/cn';

export interface SkeletonProps {
  /**
   * Required, both of them. WS-8 forbids layout shift on data arrival, and the
   * only way a placeholder can guarantee that is to occupy exactly the space
   * the real content will. A skeleton that sizes itself from its container is
   * a skeleton that jumps.
   *
   * Any CSS length: '100%', '12rem', '240px'.
   */
  width: string;
  height: string;
  radius?: 'sm' | 'md' | 'lg' | 'full' | undefined;
  className?: string | undefined;
}

const RADII = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
} as const;

/**
 * Loading placeholder.
 *
 * `aria-hidden`, always. Assistive technology should hear the region's own
 * busy state once, not a stack of meaningless boxes — put `aria-busy` on the
 * panel and let this be purely visual.
 */
export function Skeleton({ width, height, radius = 'md', className }: SkeletonProps) {
  const style: CSSProperties = { width, height };

  return (
    <span
      aria-hidden
      data-testid="skeleton"
      style={style}
      className={cn('block animate-pulse bg-surface-raised', RADII[radius], className)}
    />
  );
}
